import { Button } from "@sl/react-datafabric-components";
import { ProgressSpinner } from "primereact/progressspinner";
import { classNames } from "primereact/utils";
import { useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SettingsProvider } from "../context/settings.provider";
import { useFetchCurrentUserQuery } from "../redux/features/authorisation.redux";
import { setUserInfo } from "../redux/features/authSlice";
import { type RootAuthState, AuthContext } from "./authProvider";
import AuthPageWrapper from "./pages/components/auth-page-wrapper";
import styles from "./pages/components/auth-styles.module.css";

// Utility hook for dispatching data when authenticated
function useDispatchData(
  isAuthenticated: boolean | undefined,
  data: any,
  dispatchAction: (data: any) => void
) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (isAuthenticated && data) {
      dispatch(dispatchAction(data.data));
    }
  }, [isAuthenticated, data]);
}

export function AuthenticatedUserWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const authState = useSelector((state: RootAuthState) => state.auth);
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch essential data
  const user = useFetchCurrentUserQuery();
  // const menu = useFetchMenuQuery();
  // const settings = useFetchSettingQuery();

  // Handle authentication state
  useEffect(() => {
    if (authState.authenticated === false) {
      authContext.logout();
    }
  }, [authState.authenticated]);

  const authErrorStatus = Number((user.error as any)?.status);
  const shouldForceLogout =
    user.isError && [401, 403, 404].includes(authErrorStatus);

  // Recover from stale/invalid sessions instead of showing a hard error page.
  useEffect(() => {
    if (shouldForceLogout) {
      authContext.logout();
    }
  }, [shouldForceLogout]);

  // Set essential data into store
  useDispatchData(authState.authenticated, user.data, setUserInfo);
  // useDispatchData(authState.authenticated, menu.data, setMenu);
  // useDispatchData(authState.authenticated, settings.data, setSettings);

  const isLoading =
    user.isLoading ||
    // menu.isLoading ||
    // settings.isLoading ||
    !authState.authenticated;
  const hasError = user.isError && !shouldForceLogout;
  //  || settings.isError;
  const canRenderChildren = !isLoading && !hasError;

  if (isLoading) {
    return (
      <div
        className="flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <ProgressSpinner />
      </div>
    );
  }

  if (shouldForceLogout) {
    return (
      <div
        className="flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <ProgressSpinner />
      </div>
    );
  }

  if (hasError) {
    return (
      <AuthPageWrapper>
        <div className="px-5 min-h-screen flex justify-content-center align-items-center">
          <div className="z-1 text-center">
            <div className="text-900 font-bold text-8xl mb-4">Oops!</div>
            <p className="line-height-3 mt-0 mb-5 text-700 text-xl font-medium">
              Something went wrong
            </p>
            <Button
              label="Go back"
              icon="pi pi-home"
              className={classNames(styles.mainButton, "border-noround")}
              onClick={() => navigate(0)}
            />
          </div>
        </div>
      </AuthPageWrapper>
    );
  }

  return (
    <SettingsProvider menu={[]}>
      {canRenderChildren ? children : null}
    </SettingsProvider>
  );
}
